/**
 * English catalogue.
 *
 * Derived from the Arabic source of truth in `./ar.ts`. Typed as `Catalog`, so
 * TypeScript fails the build if a key is missing, extra, or misspelled — the
 * two locales can never silently drift apart.
 */

import type { Catalog } from "./ar";

export const en: Catalog = {
  meta: {
    localeName: "English",
    dir: "ltr",
  },

  brand: {
    company: "ATHKYA",
    product: "THKY",
    tagline: "Intelligence that works your way.",
    logoAlt: "ATHKYA",
  },

  cta: {
    primary: "Try THKY",
    secondary: "See how it works",
    signIn: "Sign in",
    signUp: "Create account",
    contact: "Contact us",
    earlyAccess: "Join early access",
    backHome: "Back to home",
    waitlist: "Join the waitlist",
    waitlistSubject: "I would like to join the THKY waitlist",
    unavailable: "Sign-up is not available yet",
  },

  nav: {
    features: "Features",
    howItWorks: "How it works",
    modes: "Processing modes",
    security: "Security & privacy",
    pricing: "Pricing",
    contact: "Contact",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    switchLanguage: "Switch to Arabic",
  },

  hero: {
    eyebrow: "By ATHKYA",
    title: "Intelligence that works your way.",
    body: "THKY isn't a chat window. It's a team of capable AI employees that understands how you speak, runs on your own machine when you want it to, and never takes a step without your say-so.",
    trust: {
      offline: "Works offline",
      onDevice: "Your data stays on your device",
      arabicFirst: "Arabic-first, not translated",
    },
  },

  preview: {
    label: "A glimpse of the THKY workspace",
    workspace: "Al Nukhba Co. workspace",
    nav: {
      home: "Home",
      assistant: "THKY Assistant",
      boardroom: "Boardroom",
      projects: "Projects",
      agents: "Agents & Departments",
      files: "Files & Knowledge",
      tasks: "Tasks",
    },
    modeLabel: "Processing mode",
    modeValue: "Local",
    modeNote: "Nothing leaves this device",
    conversation: {
      user: "Put together a launch plan for the new product next quarter.",
      assistant:
        "Understood. I'll turn this into a project brief, then convene the right specialists to review it before anything runs.",
      step1: "Turned the request into a project brief",
      step2: "Shortlisted four agents for the session",
      step3: "Waiting for your approval to begin",
    },
    composer: "Ask THKY anything — Arabic or English…",
  },

  how: {
    title: "How THKY works",
    subtitle:
      "Four clear steps. You see each one, and you decide whether it goes ahead.",
    steps: {
      describe: {
        title: "Describe",
        body: "Say what you want in Arabic or English, in your own words. No special phrasing, no commands to memorise.",
      },
      boardroom: {
        title: "Boardroom",
        body: "The right agents gather around the table and discuss your request in the open, showing their progress and the gist of their reasoning.",
      },
      approve: {
        title: "Approve",
        body: "After each round it's your call: approve, ask for changes, or stop. Nothing executes before you say so.",
      },
      execute: {
        title: "Execute",
        body: "The Project Director turns the approved decision into milestones and tasks, then sees the work through.",
      },
    },
  },

  modes: {
    title: "You decide where THKY thinks",
    subtitle:
      "Processing modes aren't a buried setting. They're your decision, and they stay visible while you work.",
    autoNote:
      "Auto picks between these routes based on your policy and what's available — and never crosses the limits you set.",
    status: {
      available: "In early access",
      comingSoon: "Later",
    },
    cloud: {
      name: "Cloud",
      body: "Processing runs on ATHKYA servers through our own gateway. You never supply API keys and never manage a provider yourself.",
      requirement: "Requires an internet connection.",
    },
    hybrid: {
      name: "Hybrid — local first",
      body: "THKY tries your machine first. It only reaches for the cloud when your policy explicitly allows it.",
      requirement: "Needs a local model; cloud is optional.",
    },
    local: {
      name: "Local",
      body: "Models run on your machine alone. Your text never leaves the device and no external service is called.",
      requirement: "Needs a local model installed on your machine.",
    },
    offline: {
      name: "Offline",
      body: "Fully fail-closed: no cloud, no connectors, no telemetry, no fallback. If it can't be done locally, THKY stops and tells you — it will not quietly reach for the internet.",
      requirement: "Runs only on what's already on your device.",
    },
  },

  workforce: {
    title: "A team, not a single tool",
    subtitle:
      "THKY's capabilities are organised into departments. Every agent has a defined role, its own permissions, tools and memory.",
    note: "You set what each department is allowed to do, and what it can and cannot see.",
    departments: {
      strategy: { name: "Strategy", role: "Turns an idea into a measurable plan" },
      marketing: { name: "Marketing", role: "Messaging, campaigns and content" },
      finance: { name: "Finance", role: "Budgets, costs and forecasts" },
      operations: { name: "Operations", role: "Process, follow-through and quality" },
      engineering: { name: "Engineering", role: "Specs and technical delivery" },
      people: { name: "People", role: "Hiring, policy and onboarding" },
    },
  },

  privacy: {
    title: "Privacy isn't a setting. It's the foundation.",
    subtitle:
      "Most AI tools ask for your trust. THKY is built so it doesn't need it — because it can do the work without ever leaving your machine.",
    points: {
      failClosed: {
        title: "Offline means offline",
        body: "In this mode there is no fallback path. If the work can't be done locally, THKY stops and says so, rather than quietly sending your data somewhere else.",
      },
      onDevice: {
        title: "Your data stays where you put it",
        body: "In local modes your files, conversations and working memory stay on your machine. We hold no copy, because we never receive one.",
      },
      visible: {
        title: "Indicators anyone can read",
        body: "At any moment you can see where processing is happening and what has left your device — in plain language, not jargon.",
      },
      noKeys: {
        title: "No keys, no provider settings",
        body: "We won't ask you for API keys or make you pick a provider. You see THKY and the processing mode, and that's all you need to.",
      },
    },
  },

  boardroom: {
    title: "The Boardroom",
    subtitle:
      "Instead of one answer from one voice, the specialists sit around the table and talk your request through in the open — then wait for your word.",
    liveLabel: "Session live",
    roundLabel: "Round",
    ofRounds: "of 3",
    approvalPrompt: "Waiting on your decision before the next round",
    actions: {
      approve: "Approve",
      revise: "Request changes",
      stop: "Stop",
    },
    agents: {
      architect: { name: "Prompt Architect", status: "Turned the request into a brief" },
      strategy: { name: "Strategy", status: "Setting metrics and risks" },
      marketing: { name: "Marketing", status: "Proposing message and channels" },
      finance: { name: "Finance", status: "Reviewing cost and viability" },
      director: { name: "Project Director", status: "Awaiting approval" },
    },
    summaryLabel: "Round summary",
    summary:
      "The agents agreed on a phased launch across three stages, concentrating spend on the highest-return channel. Finance wants a firm ceiling agreed before stage two.",
  },

  finalCta: {
    title: "Get started with THKY",
    body: "Create your workspace — personal or company — and choose how, when and where THKY works.",
    note: "Early access is open now.",
  },

  pages: {
    features: {
      title: "Features",
      subtitle: "What makes THKY different from a chat window.",
    },
    security: {
      title: "Security & privacy",
      subtitle: "How we handle your data, and what we never receive at all.",
      commitmentsTitle: "Our commitments",
      commitments: {
        one: "In local and offline modes, your data is never sent to any third party.",
        two: "Offline mode fails closed: no fallback path and no silent attempt to connect.",
        three: "Provider keys never exist in your browser, and never on your device.",
        four: "Full isolation between workspaces, with defined permissions for every member.",
        five: "An audit trail of what happened, and a clear indicator of what left the device.",
      },
      statusTitle: "What's ready today",
      statusNote:
        "We draw a clear line between what works now and what is still being built. We won't describe an unfinished feature in the present tense.",
    },
    how: {
      title: "How THKY works",
      subtitle: "From idea to execution, step by step.",
    },
    modes: {
      title: "Processing modes",
      subtitle: "Five explicit choices, and you make the call.",
    },
    pricing: {
      title: "Pricing",
      subtitle: "We haven't announced pricing yet.",
      body: "THKY is in early access. We'd rather set pricing once we understand how you actually use it than put a number up now and change it later.",
      earlyAccessTitle: "Early access",
      earlyAccessBody:
        "Join now for access ahead of general availability, and your feedback will shape how the plans are set.",
    },
    contact: {
      title: "Contact us",
      subtitle: "For enquiries, partnerships and early access.",
      emailLabel: "Email",
    },
    notFound: {
      title: "Page not found",
      subtitle: "The link you opened doesn't lead to a page.",
    },
  },

  platformNotice: {
    title: "Platform destination not configured",
    body: "VITE_PLATFORM_APP_URL isn't set in this build, so the sign-in and sign-up buttons won't go anywhere. This is a development notice and does not appear in a configured production build.",
  },

  footer: {
    tagline: "Intelligence that works your way.",
    product: "Product",
    company: "Company",
    legal: "Legal",
    privacy: "Privacy policy",
    terms: "Terms of use",
    rights: "All rights reserved.",
    emailUnset: "Contact email not configured",
  },

  legal: {
    updated: "Last updated",
    draftNotice:
      "These documents describe ATHKYA's actual data handling accurately, but they have not yet been through legal review. A final version will be published before the service is generally available.",

    privacy: {
      title: "Privacy policy",
      intro:
        "THKY is built on one rule: your data is yours. This policy explains what we actually collect — and, more importantly, what we do not.",

      localTitle: "Where your data lives",
      localBody:
        "Your conversations, files and knowledge are stored on your device in a local database. We do not upload them to our servers, we keep no copy, and we cannot read them. Delete the app and they go with it.",

      modesTitle: "Processing modes, and what each one means",
      modesBody:
        "The mode you choose decides what leaves your device. This is not a cosmetic setting — it is enforced server-side at the points where network calls are made:",
      modeLocal:
        "Local: requests are processed on your device by a local model. Your text does not leave the machine.",
      modeOffline:
        "Offline: every network call is explicitly refused — no cloud models, no connectors, no telemetry, no fallback. It fails closed: if a request cannot be served locally we say so rather than quietly reaching for the cloud.",
      modeCloud:
        "Cloud: your request text is sent through the ATHKYA gateway to a model provider to be processed and answered. It is not used to train any model.",
      modeHybrid:
        "Hybrid and Local First: tries locally first and only reaches out for what the device cannot do — and tells you when it does.",

      keysTitle: "Keys and secrets",
      keysBody:
        "We neither ask for nor accept provider API keys from you. Model access keys stay server-side; they are never sent to the browser or stored on your device. Credentials you save for your own services are stored encrypted, locally.",

      collectTitle: "What we actually collect",
      collectBody:
        "For this website: no advertising trackers, and we sell data to no one. For an account — if you create one — we keep what running it requires: your email and usage records for billing. In Offline mode no telemetry is sent at all.",

      rightsTitle: "Your rights",
      rightsBody:
        "You can view, export and delete your data at any time. Because most of it is on your device, deleting it there is an actual deletion rather than a request you wait on.",

      contactTitle: "Questions",
      contactBody: "For any privacy question, write to us at:",
    },

    terms: {
      title: "Terms of use",
      intro: "Short and direct. By using THKY you agree to the following.",

      serviceTitle: "The service",
      serviceBody:
        "THKY is an AI workspace that runs agents which work on your behalf. You choose the processing mode, and agents take no externally-visible action without your approval.",

      accountTitle: "Your account",
      accountBody:
        "You are responsible for protecting your account and for what happens through it. Tell us immediately if you believe someone else has gained access.",

      useTitle: "Acceptable use",
      useBody:
        "Do not use THKY for anything unlawful, to harm others, to attack the service or circumvent its limits, or to impersonate anyone.",

      aiTitle: "Limits of AI",
      aiBody:
        "Agents can be wrong. Do not rely on their output for a medical, legal or financial decision without a qualified professional. You decide; the agents show their reasoning so you can judge it.",

      contentTitle: "What you create",
      contentBody:
        "What you put in and what is produced for you is yours. We claim no rights in it and do not use it to train models.",

      openSourceTitle: "Open source",
      openSourceBody:
        "THKY is built on open-source software. Its licenses and attributions are preserved as their terms require.",

      changesTitle: "Changes to these terms",
      changesBody:
        "We may update these terms. We will tell you about material changes before they take effect, not after.",

      contactTitle: "Contact",
      contactBody: "For any question about these terms:",
    },
  },
};
